import React from 'react';
import { html } from 'lit';
import parse from 'html-react-parser';
import { om2confirm } from "omni-campaign-ui";

// Shared components and utilities
import { 
    OmniTableComponent, 
    OmniTileComponent, 
    OmniToolbarComponent,
    NoDataComponent, 
    Utils, 
    AlertMessagesService 
} from '../../../../shared';

// Feature imports
import { SystemParametersService } from '../../';

class SystemParametersComponent extends React.Component {

    defaultState = {
        data: [],
        formData: {},
        showSkeleton: false,
        dropdownOptions: [{ id: 'true', value: 'true' }, { id: 'false', value: 'false' }],
        dropdownOptionsBuySystem: [{ id: 'MediaOcean', value: 'MediaOcean' }, { id: 'MediaOcean-CA', value: 'MediaOcean-CA' }, { id: 'None', value: 'None' }],
        clientCodeOptions: []
    };
    
    sort = { key: '', dir: '' };

    constructor(props) {
        super(props);
        this.state = this.defaultState;
    };

    // Lifecycle methods
    componentDidMount() {
        this.props.setSpinnerCount(0);
        if(!this.props.systemParametersData?.isLoaded && !this.props.systemParametersData.data) {
            this.setState({ showSkeleton: true });
        } else {
            this.setParameterTableData(this.props.systemParametersData);
        }
    };
    
    componentWillReceiveProps(props) {
        const { systemParametersData } = props;
        this.setParameterTableData(systemParametersData);
    };
    
    /**
     * Set the system parameters data to the table data. Using keys mentioned in the `keysForShowing` array as parameter name values.
     * @param {Object} systemParametersData - The system parameters data.
     */
    setParameterTableData(systemParametersData) {
        const keysForShowing = ["clientCode", "isActive", "isCurrencyLocal", "moAgencyId", "moOrganizationId", "isApprovalRequired","buyingSystem","market", "showFlaggedFeature"]
        systemParametersData?.isLoaded && this.setState({ showSkeleton: false });
        
        let selectedSystemParametersData = systemParametersData?.dataArray?.[0] || {};

        if (this.state.formData?.clientCode) {
            selectedSystemParametersData = systemParametersData?.dataArray?.find(item => item.clientCode === this.state.formData.clientCode) || {};
        }

        if (systemParametersData && systemParametersData?.isLoaded) {
            const filteredData = Object.keys(selectedSystemParametersData).filter((key) => keysForShowing.includes(key));
            const arrayData = filteredData.map((key) => { 
                return {parameterName: key, parameterValue: selectedSystemParametersData[key], startDate:'', endDate:''} 
            });

            const clientCodeOptions = (systemParametersData?.dataArray ?? []).map((item) => {
                return { id: item.clientCode, value: item.clientCode }
            });

            this.setState({ data: arrayData, clientCodeOptions });
        }
    }

    // Showing skeleton table while data is loading
    displaySkeletonTable() {
        let rows = '';
        for (let i = 0; i < 11; i++) {
            rows = rows + '<div className="table skeleton"></div>';
        }
        rows = (html`${rows}`);
        return <>{parse(rows.values[0])}</>;
    }

    // Updaing sort column based on the event
    updateSortColumn = (event) => {
        let sortedData = [...this.state.data];

        // Updating `this.sort` with any new or changed properties.
        Object.assign(this.sort, event.detail);

        if (event.detail.dir === 'asc') { // Sorting based on the direction of the event
            if (['startDate', 'endDate'].includes(event.detail.key)) {
                sortedData = Utils.sortDateValues(event, sortedData);
            } else {
                sortedData = Utils.sortStringAndBooleanValues(event, sortedData);
            }
        } else if (event.detail.dir === 'desc') {
            sortedData = sortedData.reverse();
        }
        this.setState({ data: sortedData });
    }

    /**
     * Blurs the dropdown element when the focus is lost.
     * @param {number} key - The index of the current row.
     * @param {string} field - The field name.
     * @returns {void}
    */
    handleFieldsBlur = (key, field) => {
        const table = document.getElementById('systemParametersTable').shadowRoot
        // If the current dropdown element does not contain the active element, remove the 'opened' attribute and 'adjust-dropdown' class
        if (!table.getElementById(field + '-dropdown-' + key).contains(document.activeElement)) {
            table.getElementById(field + '-dropdown-' + key).removeAttribute('opened')
            table.getElementById(field + '-dropdown-' + key).classList.remove('adjust-dropdown')
        }
    };

    /**
     * Adjusts the position of the dropdown element based on the position of the dropdown list.
     * @param {number} key - The index of the current row.
     * @param {string} field - The field name.
     * @returns {void}
    */
    handleFieldsClick = (key, field) => {
        const table = document.getElementById('systemParametersTable').shadowRoot
        // If there is an element with the class 'adjust-dropdown', remove that class
        if (table.querySelector('.adjust-dropdown')) {
            table.querySelector('.adjust-dropdown').classList.remove('adjust-dropdown')
        }
        // Add the 'adjust-dropdown' class to the current dropdown element and adjust its position
        table.getElementById(field + '-dropdown-' + key).classList.add('adjust-dropdown')
        this.adjustDropdownPosition()
    };

    /**
     * Adjusts the position of the dropdown list based on the position of the table.
     * @returns {void}
     */
    adjustDropdownPosition() {
        const table = document.getElementById('systemParametersTable').shadowRoot
        const dropdownElement = table.querySelector(".adjust-dropdown");
        if (dropdownElement) { // check if the dropdown element is present in the DOM and adjust its position based on the table's position.
            const tableBox = table.querySelector('.table').getBoundingClientRect();
            const dropdownRect = dropdownElement.getBoundingClientRect();
            if (dropdownElement.shadowRoot !== null) {
                const dropdownList = dropdownElement.shadowRoot.querySelector(".dropdown");
                if (dropdownList) {
                    if (dropdownRect.top + dropdownList.offsetHeight + dropdownElement.offsetHeight > tableBox.bottom) {
                        dropdownList.style.top = 'auto'
                        dropdownList.style.bottom = `${ dropdownElement.offsetHeight + 2}px`;
                    } else {
                        dropdownList.style.bottom = 'auto'
                        dropdownList.style.top = `${dropdownElement.offsetHeight}px`;
                    }
                }
            }
        }

    }

    /**
     * Updates the form data based on the dropdown selection.
     * @param {Object} item - The current row item.
     * @param {Object} event - The event object.
     * @returns {void}
     */
    onFormUpdate = (field, event) => {
        let value = Utils.getValue(event);
        if (value) {
            const formData = this.state.formData;
            let currentField = this.state.data.find(val => val.parameterName == field);
            let currentValue = ["undefined", ""].includes(String(currentField?.parameterValue)) ? "" : String(currentField?.parameterValue);
            if (currentValue != value) {
                formData[field] = value;
            } else {
                delete formData[field];
            }

            if (field === 'clientCode') {
                this.setState({ formData: {clientCode: value} }, () => {
                    this.setParameterTableData(this.props.systemParametersData);
                });
            } else {
                this.setState({ formData: { ...formData } });
            }
        }
    };

    /**
     * Get dropdown value based on the parameter name. If the parameter name is present in the form data, set the dropdown value to the parameter value.
     * @param {Object} item - The current row item.
     * @param {number} index - The index of the current row.
     * @param {string} field - The field name.
     * @returns {Object} - The dropdown value.
     */
    getValueDropdown = (item, index, field) => {
        let dropdownValue = ["null", "undefined"].includes(String(item.parameterValue)) ? "" : String(item.parameterValue);
        if(this.state.formData[item?.parameterName]) {
            dropdownValue = this.state.formData[item?.parameterName];
        };

        if(["isApprovalRequired", "isActive", "isCurrencyLocal","buyingSystem", "showFlaggedFeature", "clientCode"].includes(item?.parameterName)) {
            let option = [];

            if (item?.parameterName === "buyingSystem") {
                option = this.state.dropdownOptionsBuySystem;
            } else if (item?.parameterName === "clientCode") {
                option = this.state.clientCodeOptions;
            } else {
                option = this.state.dropdownOptions;
            }

            // todo: delete later after making the campaign changes as per the client code local currency changes
            // Disable isCurrencyLocal, showFlaggedFeature, and isApprovalRequired dropdowns if not viewing the first client. 
            const firstClientCode = this.props.systemParametersData?.dataArray?.[0]?.clientCode;
            const selectedClientCode = this.state.formData?.clientCode || firstClientCode;
            const isFirstClient = selectedClientCode === firstClientCode;
            const isDisabled = ["isCurrencyLocal", "showFlaggedFeature", "isApprovalRequired"].includes(item?.parameterName) && !isFirstClient;

            return html`
            <style>
                :host::part(target) {
                    padding: 10px !important;
                }
                .name-dropdown {
                    min-width: 100px;
                    max-width: 200px;
                }
                :host::part(value-text-cell) {
                    padding: 10px;
                    color: var(--color-almost-black);
                }
            </style>
            <td part="table-body-cell">
                <omni-dropdown
                class="name-dropdown"
                hidefooter
                name=${field}
                .placeholder="Select"
                .value=${[dropdownValue]}
                id=${field + '-dropdown-' + index}
                .options=${option}
                ?disabled=${isDisabled}
                @blur=${() => this.handleFieldsBlur(index, field)}
                @click=${() => this.handleFieldsClick(index, field)}
                @change=${(event) => this.onFormUpdate(item?.parameterName, event)}>
                </omni-dropdown>
            </td>`
        } else {
           return this.getTextboxTemplate(item, index, item?.parameterName, dropdownValue);
        }
    };

    /**
     * Get textbox template based on the parameter name.
     * @param {Object} item - The current row item.
     * @param {number} index - The index of the current row.
     * @param {string} field - The field name.
     * @returns {Object} - The textbox template.
     */
    getTextboxTemplate = (item, index, field, value) => {
        return html`
        <style>
          :host::part(cell-input) {
            max-width: 200px !important;
            font-weight: 600;
        }
        </style>
        <td>
          <input id=${field + '-' + index}
           part="cell-input"
           class="input"
           type="text"
           .disabled=${field?.toLowerCase() === 'clientcode'}
           .value=${value}
           @change=${(event) => this.onFormUpdate(field, event)}
        >
        </td>`;
    }

    /**
     * Get date template based on the parameter name and split the date value.
     * @param {Object} item - The current row item.
     * @param {number} index - The index of the current row.
     * @param {string} field - The field name.
     */
    getDateTemplate = (item, index, field) => {        
        let date = item[field];
        if (date && date.includes('T')) {
            date = date.split('T')[0];
        } else if (date === 'string') {
            date = '';
        }

        let dateValue = date;
        if (this.state.formData[field]) {
            dateValue = this.state.formData[field];
        }

        const  datePlaceholder = field === 'startDate' ? 'Start Date' : 'End Date';

        if(item.parameterName === "isApprovalRequired") {
            return html`
            <style>
                input[type="date"]:before {
                    content: attr(data-date);
                    margin-left: 35px;
                    color: black;
                }

                input[type="date"]::-webkit-datetime-edit,
                input[type="date"]::-webkit-inner-spin-button,
                input[type="date"]::-webkit-clear-button {
                    display: none;  
                }

                input[type="date"]::-webkit-calendar-picker-indicator {
                    position: absolute;
                    top: 6px;
                    left: 15px;
                    width: 18px;
                    height: 18px;
                    content: 'asdasd';
                    color: black;
                    opacity: 1;
                }
            </style>
            <td id=${index + '-date'}>
                <div style="max-width: 200px;">
                    <input
                    id=${field + '-date-' + index}
                    data-date=${Utils.formatDateObject(dateValue) || `Enter ${datePlaceholder}`}
                    type="date"
                    class="input text"
                    @change=${(event) => this.onFormUpdate(field, event)}
                    .value=${Utils.formatDateObject(dateValue)}
                    />
                </div>
            </td>`
        } else {
            return html`<td part="table-body-cell">`
        }
    }

    systemParametersColumns = [
        {
            label: 'PARAMETER NAME',
            key: 'parameterName',
            passthrough: true,
            isSortable: true,
            template: (item) => {
                return html`
                <style>
                .table-container {
                    min-height: 75vh;
                }
                :host td{
                    overflow: revert !important;
                }
                :host::part(parameter-name) {
                    font-weight: 600;
                    color: var(--color-almost-black) !important;
                }
                </style>

                <td part="table-body-cell parameter-name">${item?.parameterName}</td>`
            }
        },
        {
            label: 'VALUE',
            key: 'parameterValue',
            passthrough: true,
            isSortable: true,
            template: (item, index) => this.getValueDropdown(item, index, 'parameterValue')
        },
        {
            label: 'START DATE',
            key: 'startDate',
            passthrough: true,
            isSortable: true,
            template: (item, index) => this.getDateTemplate(item, index, 'startDate')
        },
        {
            label: 'END DATE',
            key: 'endDate',
            passthrough: true,
            isSortable: true,
            template: (item, index) => this.getDateTemplate(item, index, 'endDate')

        },
    ];

    // on click of cancel button set the form data to empty
    onCancel = () => {
        this.setState({ formData: {} });
    };

    confirmationCallback = () => {
        om2confirm(
            `Changing the Buying System to "${this.state.formData?.buyingSystem}" may impact how placements are processed in the product. Do you want to continue?`,
            {
                callback: () => this.onSave(),
                type: "warning",
                title: `Update Buying System`,
            }
        );
    }

    // on click of save button save the data
    onSave = () => {
        this.setState({ showSkeleton: true });

        const selectedSystemParametersData = this.props.systemParametersData?.dataArray?.find(item => item.clientCode === (this.state.formData?.clientCode || this.state.data.filter((d) => d.parameterName === 'clientCode')[0]?.parameterValue)) || {};

        const payload = {
            ...this.state.formData,
            "clientId": selectedSystemParametersData?.clientId,
            "name": selectedSystemParametersData?.name,
            "clientCode": selectedSystemParametersData?.clientCode,
            updatedAt: new Date().toISOString(),
            updatedBy: this.props && this.props.omniProfile && this.props.omniProfile['email']
        };
        if (payload["buyingSystem"]) {
            payload["buyingSystem"] = payload["buyingSystem"] == 'Nothing' ? '' : payload["buyingSystem"];
        }
        if (Object.keys(this.state.formData)?.includes('isCurrencyLocal')) {
            payload['clientCode'] = selectedSystemParametersData?.clientCode;
        }

        // Call the API to save the data
        SystemParametersService.updateSystemParameters(payload)
            .then((response) => {
                const updatedData = Array.isArray(response?.data) ? response?.data[0] : response?.data;
                let systemParamDataArray = Array.isArray(response?.data) ? response?.data : [response?.data];
                updatedData.isLocal = updatedData?.isCurrencyLocal;
                const data = { isLoaded: true, data: updatedData, dataArray: systemParamDataArray };
                // Preserve the selected clientCode after saving
                const selectedClientCode = this.state.formData?.clientCode;
                // Keep showSkeleton true until fetchSystemParametersData completes to prevent NoDataComponent flash
                this.setState({ formData: selectedClientCode ? { clientCode: selectedClientCode } : {}, showSkeleton: true, data: [] },
                    () => {
                        this.props.fetchSystemParametersData();
                    }
                ); // Reset the form data and set the system parameters data and show alert message
                AlertMessagesService.showSuccessToastMessage('System parameter options saved successfully!');
            }).catch(() => {
                this.setState({ showSkeleton: false });
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.onSave, title: 'Unable to save system parameter options', bodyMsgArr: ['Failed to save system parameter options!'] }]);
            });
    }

    render() {
        // Filter out clientCode from formData for determining if save button should be disabled
        const formDataWithoutClientCode = Object.keys(this.state.formData)
            .filter(key => key !== 'clientCode')
            .reduce((obj, key) => {
                obj[key] = this.state.formData[key];
                return obj;
            }, {});
        
        const hasFormChanges = Object.keys(formDataWithoutClientCode).length > 0;

        return (<>
            <div className="system-parameters-container">
                    <OmniTileComponent heading="">
                        <OmniToolbarComponent slot="header" className='header-toolbar'>
                            <label slot="start">System parameters options</label>
                        </OmniToolbarComponent>
                    </OmniTileComponent>
                    {!this.state.showSkeleton ?
                        this.state.data.length > 0 ?
                            <OmniTableComponent
                                id="systemParametersTable"
                                sort={this.sort}
                                data={this.state.data}
                                key={this.state.data.length}
                                keyFn={row => row.parameterName}
                                onSortUpdate={this.updateSortColumn}
                                columns={this.systemParametersColumns}
                            />
                            : (
                                <NoDataComponent
                                    heading={"No Data Found"}
                                    message={"No system parameters options found!"}
                                />
                            )
                        : this.displaySkeletonTable()
                    }
                <div className="system-parameters-footer-buttons">
                    <button className="tertiary" onClick={this.onCancel} disabled={!hasFormChanges}>Cancel</button>
                    <button disabled={!hasFormChanges} onClick={this.state.formData?.buyingSystem ? this.confirmationCallback : this.onSave}>Save</button>
                </div>
            </div>
        </>)
    }
}

export default SystemParametersComponent;